<template>
  <v-row class="main-features__list">
    <v-col
      cols="12"
      md="8"
      class=""
    >
      <v-sheet
        rounded
        class="px-4 pt-8 pb-6 h-100"
        elevation="0"
        style="border:2px solid rgba(var(--v-border-color), var(--v-border-opacity))"
      >
        <b class="text-h4 mb-4 d-flex ">
          <v-avatar
            color="info"
            class="mr-6"
            size="large"
          >
            <div>1</div>
          </v-avatar>

          <span class="text-amber-darken-5">
            Reducing VPN Detectability:
          </span>
        </b>
        <ul class="mt-4 mb-0">
          <li class="main-features__list-item">
            <v-chip
              color="green-accent-4"
              size="large"
              class="mr-2"
            >
              New
            </v-chip>

            <b>Advanced DPI Protection:</b> Emulate QUIC and DNS packets to reliably evade deep packet inspection (DPI), configurable globally or per-profile.
          </li>
          <li class="main-features__list-item">
            <v-chip
              color="green-accent-4"
              size="large"
              class="mr-2"
            >
              New
            </v-chip>

            <b>AmneziaWG 2.0 Support*:</b> Updated Amnezia methods to further enhance stealth connectivity and bypass network restrictions.
          </li>
          <li class="main-features__list-item">
            <b>Sending Junk Packets:</b> Obfuscate data patterns to thwart deep packet inspection flags.
          </li>
          <li class="main-features__list-item">
            <b>SOCKS5 Proxy Handshake:</b> Route initial handshake traffic through a SOCKS5 proxy to mask VPN connections.
          </li>
        </ul>
        <div class="text-caption mt-1 text-medium-emphasis">
          * Note: We do not support standard AWG 1.5 <code>I1</code>-<code>I5</code> parameters. Instead, WireSock uses its own,
          more convenient method for configuring simulation settings in both the configuration file and the UI.
          <a
            href="/documentation/wiresock-secure-connect/network-settings.html#protocol-masking"
            class="text-grey text-decoration-underline font-weight-bold"
          >See Documentation
          </a>
        </div>
        <div class="text-body-2 mt-3">
          <b>Need a server?</b> Our open-source installer sets up an AmneziaWG 2.0 server on Debian/Ubuntu in a few minutes, including
          <code>amneziawg-proxy</code> for bidirectional protocol imitation with WireSock Secure Connect 3.6+:
          <nuxt-link
            to="/wiresock-awg-server-tools"
            class="text-decoration-underline font-weight-bold"
          >AmneziaWG Server Tools</nuxt-link>
        </div>

      </v-sheet>
    </v-col>
<!--    <v-col-->
<!--      cols="12"-->
<!--      md="3"-->
<!--      class="main-features__list-item"-->
<!--    >-->
<!--      <feature-item-->
<!--        title="Service-Based Architecture:"-->
<!--        num="2"-->
<!--      >-->
<!--        A dedicated connectivity service runs fully isolated. Connect at system boot before user log-on, while the UI interface remains entirely independent and-->
<!--        can be fully closed.-->
<!--      </feature-item>-->
<!--    </v-col>-->
    <v-col
      cols="12"
      md="4"
      class="main-features__list-item "
    >
      <feature-item
        title="User-Level Tunnel Activation:"
        num="2"
      >
        Launch and manage tunnels, including Virtual Adapter Mode and TCP Socket Termination, safely with only user privileges, without needing administrator
        rights.
      </feature-item>
    </v-col>

    <v-col
      cols="12"
      md="4"
      class="main-features__list-item "
    >
      <feature-item
        title="Global & Selective Application Tunneling:"
        num="3"
      >
        Configure global split tunneling rules applied seamlessly across all your profiles, or precisely route only chosen apps with
        <v-code
          tag="span"
          class="bg-amber-lighten-4 font-weight-medium text-no-wrap"
        >Tunneled
        </v-code>
        /
        <v-code
          tag="span"
          class="bg-amber-lighten-4 font-weight-medium text-no-wrap"
        >Non-tunneled applications
        </v-code>
        .
        <a
          href="/documentation/guide/route-one-app-through-wireguard-windows.html"
          class="text-decoration-underline font-weight-bold"
        >Step-by-step guide</a>
      </feature-item>
    </v-col>
    <v-col
      cols="12"
      md="4"
      class="main-features__list-item "
    >
      <feature-item
        title="Intelligent IP Routing:"
        num="4"
      >
        Fine-tune your setup by excluding particular subnets from
        <v-code
          tag="span"
          class="bg-amber-lighten-4 font-weight-medium text-no-wrap"
        >Tunneled networks
        </v-code>
        with
        <v-code
          tag="span"
          class="bg-amber-lighten-4 font-weight-medium text-no-wrap"
        >Non-tunneled networks
        </v-code>
        ensuring unwanted routes are kept out of the VPN tunnel.
      </feature-item>
    </v-col>

    <v-col
      cols="12"
      md="4"
      class="main-features__list-item "
    >
      <feature-item
        title="Kill Switch / Network Lock:"
        num="5"
      >
        Protect your privacy with a rigorous Network Lock. If the VPN connection drops, all internet traffic is instantly blocked to prevent accidental data
        leaks.
      </feature-item>
    </v-col>

    <v-col
      cols="12"
      md="6"
      class="main-features__list-item "
    >
      <feature-item
        title="High-Speed Connectivity:"
        num="6"
      >
        Achieve outstanding throughput that outperforms many other user-space VPNs, matching kernel-mode speeds
        for streaming, gaming, and video conferencing.
        <div>
          See the comparison charts
          <a
            href="#performance"
            @click.prevent.stop="scrollToPerformance"
            class="text-primary"
          >below</a>
        </div>
      </feature-item>
    </v-col>

    <v-col
      cols="12"
      md="6"
      class="main-features__list-item "
    >
      <feature-item
        title="Transparent Operation:"
        num="7"
      >
        Operate in a virtual network adapter mode, or choose a default mode that preserves your current network
        settings for an uninterrupted VPN experience.
      </feature-item>
    </v-col>

    <v-col
      cols="12"
      md="5"
      class="main-features__list-item "
    >
      <feature-item
        title="Ultra-Lightweight:"
        num="8"
      >
        <p>
          For <strong>command-line installations</strong> from the SDK, WireSock Secure Connect requires just ~4.5&nbsp;MB of storage,
          with a minimal RAM footprint under 10&nbsp;MB.
        </p>
        <p class="mb-0">
          Even with the built-in UI client, the installer remains remarkably compact at ~17&nbsp;MB (excluding .NET runtime), ensuring your system stays fast.
        </p>
      </feature-item>
    </v-col>

    <v-col
      cols="12"
      md="4"
      class="main-features__list-item "
    >
      <feature-item
        title="Command-Line Interface:"
        num="9"
      >
        Manage connections, export or import profiles entirely via the powerful new CLI (connect, disconnect, import, export), enabling full headless operation.
      </feature-item>
    </v-col>

    <v-col
      cols="12"
      md="3"
      class="main-features__list-item "
    >
      <feature-item
        title="Windows 10 Mobile Hotspot Compatibility:"
        num="10"
      >
        Extend secure connectivity to devices connected via Windows 10 Mobile Hotspot—a unique feature not offered
        by the official WireGuard for Windows.
      </feature-item>
    </v-col>

<!--    <v-col-->
<!--      cols="12"-->
<!--      md="7"-->
<!--      offset-md="3"-->
<!--      class="main-features__list-item"-->
<!--    >-->
<!--      <feature-item-->
<!--        title="Backup Tunnel Configuration:"-->
<!--        num="12"-->
<!--      >-->
<!--        Stay online without interruption by switching to a secondary WireGuard setup if your primary tunnel drops.-->
<!--      </feature-item>-->

<!--    </v-col>-->

  </v-row>

</template>

<script
  setup
  lang="ts"
>

import FeatureItem from '~/components/features/feature-item.vue';

const scrollToPerformance = () => {
  const performanceBlock = document.getElementById('performance');
  performanceBlock?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
};
</script>
